<template>
  <div>
    <v-card class="rounded-lg" min-height="700px">
      <v-card-title class="headline d-flex justify-space-between align-center pb-2">
        <span>Excel Import Wizard</span>
      </v-card-title>

      <v-stepper v-model="step" class="elevation-0">
        <v-stepper-header class="elevation-0 border-bottom">
          <v-stepper-step :complete="step > 1" step="1" class="font-weight-medium">
            Select File
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2" class="font-weight-medium">
            Preview & Validate
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1" class="pa-5">
            <div
              class="border-dashed rounded-lg pa-10 d-flex flex-column align-center justify-center cursor-pointer mb-6"
              :style="dragOver ? 'border: 2px dashed #1976D2; background-color: #E3F2FD;' : 'border: 2px dashed #BDBDBD; background-color: #FAFAFA;'"
              @click="triggerFileSelect"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onFileDrop"
            >
              <v-icon size="64" color="success" class="mb-4">mdi-microsoft-excel</v-icon>
              <span class="font-weight-medium text-subtitle-1 mb-1">Drag and drop to upload (Excel)</span>
              <span class="textLight--text caption mb-2">or click to browse your files</span>
              <div class="d-flex align-center mt-2">
                <span class="textLight--text caption">Maximum file size: 2MB</span>
                <v-icon x-small color="textLight" class="ms-1">mdi-help-circle-outline</v-icon>
              </div>
            </div>

            <div style="display: none;">
              <FileSelector
                ref="fileSelector"
                accept=".xlsx,.xls"
                @onFileSelect="onFileSelect"
              />
            </div>

            <div class="text-start mb-6">
              <span class="textLight--text subtitle-2">
                Download a <a href="#" class="primary--text font-weight-medium" @click.prevent="downloadSample">sample Excel file</a> and compare it to your import file to ensure you have the file perfect for the import.
              </span>
            </div>

            <v-card flat class="grey lighten-4 pa-4 rounded-lg text-start">
              <div class="d-flex align-center mb-2 font-weight-medium text-subtitle-2">
                <v-icon color="warning" class="me-2" size="20">mdi-lightbulb-on-outline</v-icon>
                Tips
              </div>
              <ul class="text-subtitle-2 pl-4 textLight--text font-weight-regular" style="list-style-type: disc;">
                <li class="mb-1">Required columns: Organization*, Trainer*, Course*, Course Start Date*, Course End Date*, CertifiCate Days*, Employee Name*, Employee Id*.</li>
                <li>Employee Id + Course combination must be unique in the imported file.</li>
              </ul>
            </v-card>
          </v-stepper-content>

          <v-stepper-content step="2" class="pa-5">
            <div class="d-flex flex-wrap align-center justify-space-between mb-4">
              <div>
                <div class="subtitle-2 textLight--text">Rows parsed: {{ previewRows.length }}</div>
                <div class="subtitle-2 textLight--text">Valid rows: {{ validRowCount }}</div>
              </div>
              <div class="subtitle-2 textLight--text">File: {{ fileName || '-' }}</div>
            </div>

            <div v-if="importSuccess && previewRows.length === 0" class="mb-4">
              <v-alert type="success" colored-border border="left" dense text>
                All rows were imported successfully.
              </v-alert>
            </div>

            <div v-if="previewRows.length > 0" class="overflow-x-auto">
              <v-simple-table class="border rounded-lg mb-6">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-start">#</th>
                      <th class="text-start">Status</th>
                      <th class="text-start" v-for="header in previewHeaders" :key="header">{{ header }}</th>
                      <th class="text-start">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in previewRows" :key="idx">
                      <td>{{ idx + 1 }}</td>
                      <td>
                        <v-icon :color="row.isValid ? 'success' : 'error'">
                          {{ row.isValid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                      </td>
                      <td v-for="header in previewHeaders" :key="header">
                        {{ formatCell(row.rowData[header], header) }}
                      </td>
                      <td>
                        <span v-if="row.errors.length" class="error--text subtitle-2 font-weight-medium">
                          {{ row.errors.join(', ') }}
                        </span>
                        <span v-else class="success--text subtitle-2">Valid</span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <v-card-actions class="pa-0">
              <v-btn v-if="!importSuccess" outlined color="primary" @click="resetState" class="rounded-lg px-4">
                Back
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                depressed
                class="rounded-lg px-4"
                :disabled="(!hasValidRows && !importSuccess) || (apiLoading && !importSuccess)"
                @click="importSuccess ? resetState() : confirmImport()"
              >
                <span v-if="apiLoading && !importSuccess">Importing...</span>
                <span v-else-if="importSuccess">OK</span>
                <span v-else>Confirm Import ({{ validRowCount }} rows)</span>
              </v-btn>
            </v-card-actions>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>
  </div>
</template>

<script>
import FileSelector from "@/app/core-module/components/FileSelector";
import { excelExport, excelImport } from "../../reports-module/methods/excelExport";
import { importTrainingRegistrationRows } from "../methods/importTrainingRegistrationRows";

const fieldAliases = {
  organization: [
    "Organization*",
    "Organization",
    "Organization Name",
    "Company",
    "Company Name",
    "الجهة",
    "الشركة",
    "المؤسسة"
  ],
  trainer: [
    "Trainer*",
    "Trainer",
    "Trainer Name",
    "المدرب"
  ],
  course: [
    "Course*",
    "Course",
    "Course Name",
    "Training",
    "Training Name",
    "اسم الدورة",
    "الدورة"
  ],
  courseStartDate: [
    "Course Start Date*",
    "Course Start Date",
    "Course Start Dat",
    "Start Date",
    "StartDate",
    "تاريخ البدء",
    "تاريخ الدورة",
    "Date",
    "تاريخ"
  ],
  courseEndDate: [
    "Course End Date*",
    "Course End Date",
    "End Date",
    "EndDate",
    "تاريخ الانتهاء",
    "تاريخ الانتهاء",
    "تاريخ النهاية"
  ],
  type: [
    "Type",
    "type"
  ],
  certificateDays: [
    "CertifiCate Days*",
    "CertifiCate Days",
    "Certificate Days",
    "CertificateDays",
    "Validity",
    "صلاحية الشهادة",
    "المدة",
    "صلاحية"
  ],
  employeeName: [
    "Employee Name*",
    "Employee Name",
    "Employee",
    "Name",
    "employeeName",
    "اسم الموظف",
    "الاسم"
  ],
  employeeId: [
    "Employee Id*",
    "Employee Id",
    "Employee ID",
    "Civil ID or Passport No",
    "Civil ID/Passport",
    "Civil ID",
    "Passport",
    "civilIdOrPassport",
    "رقم البطاقة المدنية / جواز السفر",
    "رقم البطاقة المدنية"
  ]
};

const normalize = value =>
  String(value || "")
    .toLowerCase()
    .replace(/\*/g, "")
    .replace(/\s+/g, "")
    .trim();

const findValue = (row, keys, headers) => {
  const normalizedKeys = keys.map(normalize);
  for (const header of headers) {
    const normalizedHeader = normalize(header);
    if (normalizedKeys.includes(normalizedHeader)) {
      return row[header];
    }
  }
  return null;
};

const dateHeaderAliases = new Set(
  [...fieldAliases.courseStartDate, ...fieldAliases.courseEndDate].map(normalize)
);

const certificateDaysHeaderAliases = new Set(fieldAliases.certificateDays.map(normalize));

export default {
  name: "ExcelRegistrationImport",
  components: {
    FileSelector,
  },
  data() {
    return {
      step: 1,
      dragOver: false,
      previewRows: [],
      previewHeaders: [],
      fileName: "",
      apiLoading: false,
      importSuccess: false,
    };
  },
  computed: {
    validRowCount() {
      return this.previewRows.filter(r => r.isValid).length;
    },
    hasValidRows() {
      return this.validRowCount > 0;
    },
  },
  methods: {
    triggerFileSelect() {
      if (this.$refs.fileSelector) {
        this.$refs.fileSelector.selectFile();
      }
    },
    onFileDrop(e) {
      this.dragOver = false;
      const files = e.dataTransfer.files;
      if (files?.length) {
        const file = files[0];
        if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
          this.onFileSelect({ blob: file, name: file.name });
        } else {
          this.$toast.error("Only Excel files (.xlsx, .xls) are allowed!");
        }
      }
    },
    isDateHeader(header) {
      return dateHeaderAliases.has(normalize(header));
    },
    parseExcelDate(dateVal) {
      if (!dateVal && dateVal !== 0) return null;

      if (dateVal instanceof Date) {
        return this.$moment.utc(dateVal).format("YYYY-MM-DD");
      }

      if (typeof dateVal === "number") {
        const utcDate = new Date(Date.UTC(1970, 0, 1) + (dateVal - 25569) * 86400 * 1000);
        return this.$moment.utc(utcDate).format("YYYY-MM-DD");
      }

      const parsed = this.$moment(
        String(dateVal).trim(),
        [
          "DD/MM/YYYY",
          "MM/DD/YYYY",
          "DD-MM-YYYY",
          "MM-DD-YYYY",
          "YYYY-MM-DD",
          "DD MMM YYYY",
          "D MMM YYYY",
          "DD-MMM-YYYY",
          "D-MMM-YYYY",
          "DD MMMM YYYY",
          "D MMMM YYYY"
        ],
        true // strict parsing
      );

      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
    },
    resetState() {
      this.step = 1;
      this.dragOver = false;
      this.previewRows = [];
      this.previewHeaders = [];
      this.fileName = "";
      this.apiLoading = false;
      this.importSuccess = false;
    },
    formatCell(value, header) {
      if (value === null || value === undefined) return "";
      if (header && this.isDateHeader(header)) {
        const parsed = this.parseExcelDate(value);
        return parsed || String(value);
      }
      return String(value);
    },
    downloadSample() {
      const sampleRows = [
        {
          "Organization*": "CREWPETRO",
          "Trainer*": "BASIL SALEEM",
          "Course*": "GAS CONDITIONING AND PROCESSING",
          "Course Start Date*": "09/07/2026",
          "Course End Date*": "15/07/2026",
          "CertifiCate Days*": 12,
          "Employee Name*": "John Doe",
          "Employee Id*": "123456789",
          "Department": "IT Department",
          "Designation": "Software Engineer",
          "Type": "OFFLINE"
        }
      ];
      excelExport(sampleRows, "sample-import-registrations");
    },
    async onFileSelect(file) {
      try {
        const jsonArray = await excelImport(file.blob);
        if (!jsonArray || !jsonArray.length) {
          this.$toast.error("Failed to read file or file is empty!");
          return;
        }

        const cleanedRows = jsonArray
          .map(row => {
            const hasValue = Object.values(row).some(
              val => val !== undefined && val !== null && String(val).trim() !== ""
            );
            return hasValue ? row : null;
          })
          .filter(Boolean);

        if (!cleanedRows.length) {
          this.$toast.error("Your Excel file contains only empty rows.");
          return;
        }

        this.fileName = file.name || "";
        this.previewHeaders = Object.keys(cleanedRows[0]);

        const duplicateChecker = new Set();
        const rows = cleanedRows.map((row, index) => {
          const rowData = {};
          this.previewHeaders.forEach(header => {
            const cellValue = row[header] !== undefined ? row[header] : "";
            rowData[header] = this.isDateHeader(header)
              ? this.parseExcelDate(cellValue) || cellValue
              : cellValue;
          });

          const organization = findValue(row, fieldAliases.organization, this.previewHeaders);
          const trainer = findValue(row, fieldAliases.trainer, this.previewHeaders);
          const course = findValue(row, fieldAliases.course, this.previewHeaders);
          const courseStartDateRaw = findValue(row, fieldAliases.courseStartDate, this.previewHeaders);
          const courseEndDateRaw = findValue(row, fieldAliases.courseEndDate, this.previewHeaders);
          const courseStartDate = this.parseExcelDate(courseStartDateRaw) || (courseStartDateRaw ? String(courseStartDateRaw).trim() : "");
          const courseEndDate = this.parseExcelDate(courseEndDateRaw) || (courseEndDateRaw ? String(courseEndDateRaw).trim() : "");
          let certificateDays = findValue(row, fieldAliases.certificateDays, this.previewHeaders);
          const employeeName = findValue(row, fieldAliases.employeeName, this.previewHeaders);
          const employeeId = findValue(row, fieldAliases.employeeId, this.previewHeaders);
          const type = findValue(row, fieldAliases.type, this.previewHeaders);

          if (certificateDays !== undefined && certificateDays !== null) {
            const trimmed = String(certificateDays).trim();
            certificateDays = trimmed === "" ? 0 : Number(trimmed);
            if (Number.isNaN(certificateDays)) {
              certificateDays = 0;
            }
          } else {
            certificateDays = 0;
          }

          const certificateDaysHeader = this.previewHeaders.find(header => certificateDaysHeaderAliases.has(normalize(header)));
          if (certificateDaysHeader) {
            rowData[certificateDaysHeader] = certificateDays;
          }

          const errors = [];
          if (!organization || String(organization).toString().trim() === "") errors.push("Organization is required");
          if (!trainer || String(trainer).toString().trim() === "") errors.push("Trainer is required");
          if (!course || String(course).toString().trim() === "") errors.push("Course is required");
          if (!courseStartDate || String(courseStartDate).toString().trim() === "") errors.push("Course Start Date is required");
          if (!courseEndDate || String(courseEndDate).toString().trim() === "") errors.push("Course End Date is required");
          if (certificateDays === null || certificateDays === undefined) {
            certificateDays = 0;
          }
          if (String(certificateDays).trim() === "" && certificateDays !== 0) errors.push("CertifiCate Days is required");
          if (!employeeName || String(employeeName).toString().trim() === "") errors.push("Employee Name is required");
          if (!employeeId || String(employeeId).toString().trim() === "") errors.push("Employee Id is required");

          if (courseStartDate && !this.parseExcelDate(courseStartDate)) {
            errors.push("Invalid Course Start Date");
          }
          if (courseEndDate && !this.parseExcelDate(courseEndDate)) {
            errors.push("Invalid Course End Date");
          }

          const pairKey = `${String(employeeId || "").trim().toLowerCase()}||${String(course || "").trim().toLowerCase()}`;
          if (employeeId && course) {
            if (duplicateChecker.has(pairKey)) {
              errors.push("Duplicate Employee Id + Course row");
            } else {
              duplicateChecker.add(pairKey);
            }
          }

          return {
            rowData,
            errors,
            isValid: errors.length === 0,
            organization,
            trainer,
            course,
            courseStartDate,
            courseEndDate,
            certificateDays,
            employeeName,
            employeeId,
            type,
            rowIndex: index + 1,
          };
        });

        this.previewRows = rows;
        this.step = 2;
        this.$emit("parsed", { firstRow: cleanedRows[0], previewHeaders: this.previewHeaders });
      } catch (e) {
        this.$toast.error(e.message || "Some error occurred!");
      }
    },
    async confirmImport() {
      const validRows = this.previewRows.filter(r => r.isValid);
      if (!validRows.length) {
        return;
      }

      const payload = validRows.map(r => ({
        organization: r.organization ? String(r.organization).trim() : "",
        trainer: r.trainer ? String(r.trainer).trim() : "",
        course: r.course ? String(r.course).trim() : "",
        courseStartDate: r.courseStartDate ? String(r.courseStartDate).trim() : "",
        courseEndDate: r.courseEndDate ? String(r.courseEndDate).trim() : "",
        certificateDays: r.certificateDays ? parseInt(r.certificateDays, 10) : 0,
        employeeName: r.employeeName ? String(r.employeeName).trim() : "",
        employeeId: r.employeeId ? String(r.employeeId).trim() : "",
        type: r.type ? String(r.type).trim() : "",
        department: String(r.rowData.Department || r.rowData.department || "").trim(),
        designation: String(r.rowData.Designation || r.rowData.designation || "").trim(),
        rowData: r.rowData,
        rowIndex: r.rowIndex ? parseInt(r.rowIndex, 10) : 0,
      }));

      try {
        this.apiLoading = true;
        const { data, error } = await importTrainingRegistrationRows(payload);

        if (error) {
          throw new Error(error);
        }

        const errorsByRowIndex = new Map((data.rowErrors || []).map(item => [item.rowIndex, item.errors]));

        // Remove successfully imported rows from UI
        this.previewRows = this.previewRows.filter(row => {
          if (!row.isValid) return true; // Keep initially invalid rows
          return errorsByRowIndex.has(row.rowIndex); // Keep rows that failed in API
        }).map(row => {
          if (errorsByRowIndex.has(row.rowIndex)) {
            return {
              ...row,
              errors: errorsByRowIndex.get(row.rowIndex),
              isValid: false,
            };
          }
          return row;
        });

        // Determine succeeded rows
        const succeededRows = data.rowErrors?.length
          ? validRows.filter(r => !errorsByRowIndex.has(r.rowIndex))
          : validRows;

        if (succeededRows.length > 0) {
          this.$emit("imported", succeededRows.map(r => ({
            name: String(r.employeeName).trim(),
            civilIdOrPassport: String(r.employeeId).trim(),
            organization: String(r.organization).trim(),
            trainer: String(r.trainer).trim(),
            course: String(r.course).trim(),
            courseStartDate: String(r.courseStartDate).trim(),
            courseEndDate: String(r.courseEndDate).trim(),
            certificateDays: String(r.certificateDays).trim(),
            rowData: r.rowData,
          })));
        }

        if (data.rowErrors?.length) {
          this.$toast.error(
            `Import completed with ${data.rowErrors.length} row(s) containing errors.`
          );
        } else {
          this.$toast.success(`Imported ${data.savedCount ?? payload.length} rows successfully.`);
        }

        this.importSuccess = true;
      } catch (e) {
        console.error("Dummy import API error", e);
        this.$toast.error(e.message || "Failed to call dummy import API.");
      } finally {
        this.apiLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.border-dashed {
  border-style: dashed;
}
</style>
